<template>
  <InterviewLayout>
    <template #header>
      <InterviewHeader :brand-image="brandImage" title="INTRO" />
    </template>

    <div class="consent-prompt">
      <div class="consent-prompt__background">
        <div class="consent-prompt__container">
          <!-- eslint-disable vue/no-v-html -->
          <p class="consent-prompt__title" v-html="consentCopy" />
        </div>
      </div>
    </div>

    <template #footer>
      <InterviewFooter
        :submit-text="c('consent_prompt.button.accept')"
        :options="[
          { id: 'reject', displayText: c('consent_prompt.button.reject'), selected: false },
        ]"
        :is-loading="false"
        @continue="() => submitConsent(true)"
        @toggle-option="() => submitConsent(false)"
      />
    </template>
  </InterviewLayout>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { filterXSS } from 'xss'

import InterviewFooter from '../../../interview/components/dumb/InterviewFooter.vue'
import InterviewHeader from '../../../interview/components/dumb/InterviewHeader.vue'
import InterviewLayout from '../../../interview/components/dumb/InterviewLayout.vue'
import { c } from '../../../intl'
import type { Consent } from '../../models/Consent'

export default defineComponent({
  components: {
    InterviewLayout,
    InterviewHeader,
    InterviewFooter,
  },
  props: {
    consentPrompt: { required: true, type: Object as PropType<Consent> },
    brandImage: {
      type: [String, null],
      default: null,
    },
  },
  emits: {
    submitConsentPreference: (_doesConsent: boolean) => true,
  },
  setup(props, { emit }) {
    if (
      !props.consentPrompt.orgDataControllerEmail ||
      !props.consentPrompt.orgPrivacyPolicyUrl ||
      !props.consentPrompt.orgName
    ) {
      throw new Error('Missing one or more values from props.consentPrompt')
    }

    const toMail = (copy: string): string => {
      const sanitisedCopy = filterXSS(copy)
      return `<a href="mailto:${sanitisedCopy}">${sanitisedCopy}</a>`
    }

    const toAnchor = (copy: string): string => {
      const sanitisedCopy = filterXSS(copy)
      return `<a href="${sanitisedCopy}" target="_blank">${sanitisedCopy}</a>`
    }

    const submitConsent = (doesConsent: boolean): void => {
      emit('submitConsentPreference', doesConsent)
    }

    const consentCopyRaw = c(
      props.consentPrompt.consentType === 'VIDEO'
        ? 'consent_prompt'
        : 'consent_prompt.respondent_ids',
      {
        placeholders: {
          orgName: props.consentPrompt.orgName,
          orgPrivacyPolicyUrl: toAnchor(props.consentPrompt.orgPrivacyPolicyUrl),
          orgDataControllerEmail: toMail(props.consentPrompt.orgDataControllerEmail),
        },
        transformers: {
          toMail,
          toAnchor,
        },
      },
    )
    const consentCopy = Array.isArray(consentCopyRaw) ? consentCopyRaw.join('') : consentCopyRaw

    return {
      c,
      consentCopy,
      submitConsent,
    }
  },
})
</script>

<style lang="postcss">
.consent-prompt {
  position: relative;
  overflow: hidden;
  height: 100%;
  padding: 1rem;
  background-color: #f6f9fd;

  &__background {
    height: 100%;
    background-color: #fff;
  }

  &__container {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    align-items: center;
    border: 1px solid var(--color-blue-grey);
    background:
      linear-gradient(white 30%, rgb(255 255 255 / 0%)) center top,
      linear-gradient(rgb(255 255 255 / 0%), white 70%) center bottom,
      radial-gradient(farthest-side at 50% 0, rgb(0 0 0 / 25%), rgb(0 0 0 / 0%)) center top,
      radial-gradient(farthest-side at 50% 100%, rgb(0 0 0 / 25%), rgb(0 0 0 / 0%)) center bottom;
    background-attachment: local, local, scroll, scroll;
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 20px,
      100% 20px;
    overflow-y: scroll;
  }

  &__title {
    margin: 2rem;
    color: black;
    font-size: var(--attest-font-size-m);
    line-height: 2rem;
    text-align: left;
    white-space: pre-line;
  }
}
</style>
